import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Chip,
  Typography,
  useTheme,
  alpha,
  Tooltip,
  CircularProgress,
  Button,
  Alert,
  TextField,
  MenuItem,
  InputAdornment,
  Paper,
  Fade,
  FormControl,
  Select,
  SelectChangeEvent,
  Drawer,
  RadioGroup,
  Radio,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { keyframes } from '@mui/system';
import RefreshIcon from '@mui/icons-material/Refresh';
import UndoIcon from '@mui/icons-material/Undo';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

import ConfirmationDialog from 'components/ConfirmationDialog';
import SyncDetailDialog from './SyncDetailDialog';
import { regionSyncService } from '../services/RegionSyncService';
import { SyncHistory } from '../types';
import { logger } from 'utils/logger';

const log = logger.getLogger('HistoryTab');

const POLL_INTERVAL_MS = 5000; // Poll every 5 seconds when syncs are in progress

const pulseAnimation = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
`;

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'ROLLED_BACK', label: 'Rolled Back' },
];

// Sort key type for table columns
type SortKey = 'syncName' | 'syncType' | 'sourceAccountId' | 'targetAccountId' | 'menuId' | 'status' | 'startedAt';

const HistoryTab: React.FC = () => {
  const theme = useTheme();
  const [history, setHistory] = useState<SyncHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Sorting
  const [sortBy, setSortBy] = useState<SortKey>('startedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Temp filter state (for drawer apply/cancel)
  const [tempStatusFilter, setTempStatusFilter] = useState<string>('');
  const [tempSearchQuery, setTempSearchQuery] = useState<string>('');
  const [tempStartDate, setTempStartDate] = useState<string>('');
  const [tempEndDate, setTempEndDate] = useState<string>('');

  // Accordion expansion state
  const [expandedAccordions, setExpandedAccordions] = useState<{[key: string]: boolean}>({
    search: true,
    status: true,
    dateRange: true
  });

  // Dialog states
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<SyncHistory | null>(null);
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [historyToRollback, setHistoryToRollback] = useState<SyncHistory | null>(null);
  const [rollingBack, setRollingBack] = useState(false);

  // Auto-polling for in-progress syncs
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasInProgress = history.some(item => {
    const s = (item.status || '').toUpperCase();
    return s === 'IN_PROGRESS' || s === 'RUNNING';
  });

  // Header cell style matching Accounts page
  const headerCellStyle = {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
    py: 1.5,
    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.7)}`,
    backgroundColor: theme.palette.background.default,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04)
    }
  };

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await regionSyncService.getHistory({
        page,
        size: rowsPerPage,
        status: statusFilter || undefined
      });

      let filteredData = result.data;

      // Client-side search filter (config name, account name)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filteredData = filteredData.filter(item =>
          (item.sourceConfig || '').toLowerCase().includes(query) ||
          (item.targetConfig || '').toLowerCase().includes(query) ||
          (item.accountName && item.accountName.toLowerCase().includes(query)) ||
          (item.menuName && item.menuName.toLowerCase().includes(query)) ||
          (item.syncName && item.syncName.toLowerCase().includes(query)) ||
          String(item.accountId || '').includes(query)
        );
      }

      // Client-side date range filter
      if (startDate) {
        const start = new Date(startDate);
        filteredData = filteredData.filter(item => new Date(item.startedAt) >= start);
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        filteredData = filteredData.filter(item => new Date(item.startedAt) <= end);
      }

      // Client-side sorting
      filteredData.sort((a, b) => {
        let valueA: any;
        let valueB: any;
        switch (sortBy) {
          case 'syncName':
            valueA = (a.syncName || '').toLowerCase();
            valueB = (b.syncName || '').toLowerCase();
            break;
          case 'syncType':
            valueA = (a.syncType || '').toLowerCase();
            valueB = (b.syncType || '').toLowerCase();
            break;
          case 'sourceAccountId':
            valueA = a.sourceAccountId || 0;
            valueB = b.sourceAccountId || 0;
            break;
          case 'targetAccountId':
            valueA = a.targetAccountId || a.accountId || 0;
            valueB = b.targetAccountId || b.accountId || 0;
            break;
          case 'menuId':
            valueA = a.menuId || 0;
            valueB = b.menuId || 0;
            break;
          case 'status':
            valueA = (a.status || '').toLowerCase();
            valueB = (b.status || '').toLowerCase();
            break;
          case 'startedAt':
          default:
            valueA = new Date(a.startedAt).getTime();
            valueB = new Date(b.startedAt).getTime();
            break;
        }
        if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });

      setHistory(filteredData);
      setTotalCount(result.count);
    } catch (err: any) {
      log.error('Failed to fetch history:', err);
      setError(err.message || 'Failed to load sync history');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, searchQuery, startDate, endDate, sortBy, sortDirection]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Start/stop polling based on whether any sync is in progress
  useEffect(() => {
    if (hasInProgress) {
      // Start polling
      if (!pollIntervalRef.current) {
        log.log('In-progress sync detected — starting auto-refresh');
        pollIntervalRef.current = setInterval(() => {
          fetchHistory();
        }, POLL_INTERVAL_MS);
      }
    } else {
      // Stop polling
      if (pollIntervalRef.current) {
        log.log('No in-progress syncs — stopping auto-refresh');
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [hasInProgress, fetchHistory]);

  const handleSort = (key: SortKey) => {
    const isAsc = sortBy === key && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortBy(key);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0) {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleViewDetails = (historyItem: SyncHistory) => {
    setSelectedHistory(historyItem);
    setDetailDialogOpen(true);
  };

  const handleRollbackClick = (historyItem: SyncHistory) => {
    setHistoryToRollback(historyItem);
    setRollbackDialogOpen(true);
  };

  const handleRollbackConfirm = async () => {
    if (!historyToRollback) return;

    try {
      setRollingBack(true);
      await regionSyncService.rollbackSync(historyToRollback.id);
      setSuccess('Rollback completed successfully');
      setRollbackDialogOpen(false);
      setHistoryToRollback(null);
      await fetchHistory();
    } catch (err: any) {
      log.error('Rollback failed:', err);
      setError(err.message || 'Rollback failed');
    } finally {
      setRollingBack(false);
    }
  };

  const handleOpenFilterDrawer = () => {
    // Sync temp state with current applied filters when opening
    setTempStatusFilter(statusFilter);
    setTempSearchQuery(searchQuery);
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setFilterDrawerOpen(true);
  };

  const handleApplyFilters = () => {
    setStatusFilter(tempStatusFilter);
    setSearchQuery(tempSearchQuery);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setPage(0);
    setFilterDrawerOpen(false);
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setSearchQuery('');
    setStartDate('');
    setEndDate('');
    setTempStatusFilter('');
    setTempSearchQuery('');
    setTempStartDate('');
    setTempEndDate('');
    setPage(0);
    setFilterDrawerOpen(false);
  };

  const removeFilter = (filterKey: string) => {
    switch (filterKey) {
      case 'status':
        setStatusFilter('');
        break;
      case 'search':
        setSearchQuery('');
        break;
      case 'date':
        setStartDate('');
        setEndDate('');
        break;
    }
    setPage(0);
  };

  const hasActiveFilters = statusFilter || searchQuery || startDate || endDate;

  // Build active filter chips list
  const activeFilterChips: { key: string; label: string }[] = [];
  if (statusFilter) activeFilterChips.push({ key: 'status', label: `Status: ${statusFilter}` });
  if (searchQuery) activeFilterChips.push({ key: 'search', label: `Search: ${searchQuery}` });
  if (startDate || endDate) activeFilterChips.push({ key: 'date', label: `Date: ${startDate || '...'} – ${endDate || '...'}` });

  const getStatusIcon = (status: string) => {
    const s = (status || '').toUpperCase();
    switch (s) {
      case 'COMPLETED':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'FAILED':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'RUNNING':
      case 'IN_PROGRESS':
        return <PlayCircleIcon color="primary" fontSize="small" />;
      case 'ROLLED_BACK':
        return <UndoIcon color="warning" fontSize="small" />;
      default:
        return <HourglassEmptyIcon color="disabled" fontSize="small" />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    const s = (status || '').toUpperCase();
    switch (s) {
      case 'COMPLETED':
        return 'success';
      case 'FAILED':
        return 'error';
      case 'RUNNING':
      case 'IN_PROGRESS':
        return 'info';
      case 'ROLLED_BACK':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Pagination info text (matching Accounts style)
  const getPaginationInfo = () => {
    if (history.length === 0) return '0 items';
    const startItem = page * rowsPerPage + 1;
    const endItem = Math.min(page * rowsPerPage + history.length, totalCount);
    return `${startItem} - ${endItem} of ${totalCount} items`;
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Fade in={true} timeout={600}>
        <Paper
          elevation={2}
          sx={{
            width: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)'
            }
          }}
        >
          {/* Toolbar - matching Accounts page toolbar */}
          <Box
            p={2.5}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              background: theme.palette.background.default
            }}
          >
            <Box display="flex" alignItems="center">
              {hasInProgress && (
                <Chip
                  icon={<CircularProgress size={12} />}
                  label="Auto-refreshing"
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{
                    height: 24,
                    mr: 1,
                    borderRadius: '16px',
                    fontWeight: 500,
                    transition: 'all 0.2s'
                  }}
                />
              )}
              <Box display="flex" flexWrap="wrap" alignItems="center" mr={1}>
                {activeFilterChips.map((chip) => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    size="small"
                    onDelete={() => removeFilter(chip.key)}
                    sx={{
                      mr: 0.7,
                      mb: 0.5,
                      borderRadius: '16px',
                      fontWeight: 500,
                      transition: 'all 0.2s',
                      '&:hover': {
                        opacity: 0.9,
                        transform: 'translateY(-1px)'
                      }
                    }}
                  />
                ))}
              </Box>
              <Tooltip title="Filter history">
                <IconButton
                  size="medium"
                  onClick={handleOpenFilterDrawer}
                  sx={{
                    ml: 0.5,
                    borderRadius: '12px',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1)
                    }
                  }}
                >
                  <FilterAltIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton
                  size="medium"
                  onClick={fetchHistory}
                  disabled={loading}
                  sx={{
                    ml: 0.5,
                    borderRadius: '12px',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1)
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Filter Drawer - matching Accounts page FilterDrawer */}
          <Drawer
            anchor="right"
            open={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
            PaperProps={{
              sx: {
                width: { xs: '100%', sm: 340 },
                maxWidth: '100%',
                borderTopLeftRadius: { xs: 0, sm: 16 },
                borderBottomLeftRadius: { xs: 0, sm: 16 },
                boxShadow: '0 0 20px rgba(0,0,0,0.15)'
              }
            }}
          >
            <Box
              sx={{
                p: 2.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                pb={2}
                borderBottom={`1px solid ${alpha(theme.palette.divider, 0.7)}`}
              >
                <Box display="flex" alignItems="center">
                  <FilterAltIcon
                    color="primary"
                    sx={{ mr: 1.5, fontSize: '1.5rem' }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main
                    }}
                  >
                    Filter History
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => setFilterDrawerOpen(false)}
                  sx={{
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.08),
                      color: theme.palette.error.main
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Box sx={{ mb: 2 }}>
                {/* Search Accordion */}
                <Accordion
                  expanded={expandedAccordions.search}
                  onChange={() => setExpandedAccordions(prev => ({ ...prev, search: !prev.search }))}
                  elevation={0}
                  sx={{
                    mb: 1.5,
                    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    borderRadius: '10px !important',
                    '&:before': { display: 'none' },
                    '& .MuiAccordionSummary-root': {
                      borderRadius: '10px',
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04)
                      }
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: expandedAccordions.search ? theme.palette.primary.main : 'inherit', transition: 'all 0.3s' }} />
                    }
                    sx={{ backgroundColor: expandedAccordions.search ? alpha(theme.palette.primary.main, 0.04) : 'transparent' }}
                  >
                    <Typography sx={{ fontWeight: 600, color: expandedAccordions.search ? theme.palette.primary.main : 'inherit' }}>
                      Search
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Name, config, account, menu..."
                      value={tempSearchQuery}
                      onChange={(e) => setTempSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                        sx: {
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.light
                          }
                        }
                      }}
                    />
                  </AccordionDetails>
                </Accordion>

                {/* Status Accordion */}
                <Accordion
                  expanded={expandedAccordions.status}
                  onChange={() => setExpandedAccordions(prev => ({ ...prev, status: !prev.status }))}
                  elevation={0}
                  sx={{
                    mb: 1.5,
                    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    borderRadius: '10px !important',
                    '&:before': { display: 'none' },
                    '& .MuiAccordionSummary-root': {
                      borderRadius: '10px',
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04)
                      }
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: expandedAccordions.status ? theme.palette.primary.main : 'inherit', transition: 'all 0.3s' }} />
                    }
                    sx={{ backgroundColor: expandedAccordions.status ? alpha(theme.palette.primary.main, 0.04) : 'transparent' }}
                  >
                    <Typography sx={{ fontWeight: 600, color: expandedAccordions.status ? theme.palette.primary.main : 'inherit' }}>
                      Status
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <RadioGroup
                      value={tempStatusFilter}
                      onChange={(e) => setTempStatusFilter(e.target.value)}
                    >
                      {STATUS_OPTIONS.map(option => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={
                            <Radio
                              color="primary"
                              sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {option.label}
                            </Typography>
                          }
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </RadioGroup>
                  </AccordionDetails>
                </Accordion>

                {/* Date Range Accordion */}
                <Accordion
                  expanded={expandedAccordions.dateRange}
                  onChange={() => setExpandedAccordions(prev => ({ ...prev, dateRange: !prev.dateRange }))}
                  elevation={0}
                  sx={{
                    mb: 1.5,
                    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    borderRadius: '10px !important',
                    '&:before': { display: 'none' },
                    '& .MuiAccordionSummary-root': {
                      borderRadius: '10px',
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04)
                      }
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: expandedAccordions.dateRange ? theme.palette.primary.main : 'inherit', transition: 'all 0.3s' }} />
                    }
                    sx={{ backgroundColor: expandedAccordions.dateRange ? alpha(theme.palette.primary.main, 0.04) : 'transparent' }}
                  >
                    <Typography sx={{ fontWeight: 600, color: expandedAccordions.dateRange ? theme.palette.primary.main : 'inherit' }}>
                      Date Range
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 2 }}>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={tempStartDate}
                      onChange={(e) => setTempStartDate(e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      value={tempEndDate}
                      onChange={(e) => setTempEndDate(e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>

              <Box mt="auto">
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleApplyFilters}
                  startIcon={<FilterAltIcon />}
                  sx={{
                    py: 1,
                    mb: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 15px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  onClick={handleClearFilters}
                  startIcon={<CleaningServicesIcon />}
                  sx={{
                    py: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.secondary.main, 0.08)
                    }
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Box>
          </Drawer>

          {/* Table */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellStyle}>
                        <TableSortLabel
                          active={sortBy === 'syncName'}
                          direction={sortBy === 'syncName' ? sortDirection : 'asc'}
                          onClick={() => handleSort('syncName')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: sortBy === 'syncName' ? `${theme.palette.primary.main} !important` : undefined
                            }
                          }}
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={headerCellStyle}>
                        <TableSortLabel
                          active={sortBy === 'syncType'}
                          direction={sortBy === 'syncType' ? sortDirection : 'asc'}
                          onClick={() => handleSort('syncType')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: sortBy === 'syncType' ? `${theme.palette.primary.main} !important` : undefined
                            }
                          }}
                        >
                          Type
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={headerCellStyle}>
                        <TableSortLabel
                          active={sortBy === 'sourceAccountId'}
                          direction={sortBy === 'sourceAccountId' ? sortDirection : 'asc'}
                          onClick={() => handleSort('sourceAccountId')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: sortBy === 'sourceAccountId' ? `${theme.palette.primary.main} !important` : undefined
                            }
                          }}
                        >
                          Source Acct
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={headerCellStyle}>
                        <TableSortLabel
                          active={sortBy === 'targetAccountId'}
                          direction={sortBy === 'targetAccountId' ? sortDirection : 'asc'}
                          onClick={() => handleSort('targetAccountId')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: sortBy === 'targetAccountId' ? `${theme.palette.primary.main} !important` : undefined
                            }
                          }}
                        >
                          Target Acct
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={headerCellStyle}>
                        <TableSortLabel
                          active={sortBy === 'menuId'}
                          direction={sortBy === 'menuId' ? sortDirection : 'asc'}
                          onClick={() => handleSort('menuId')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: sortBy === 'menuId' ? `${theme.palette.primary.main} !important` : undefined
                            }
                          }}
                        >
                          Menu ID
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={headerCellStyle}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Stats
                          <Tooltip
                            title={
                              <Box sx={{ p: 0.5 }}>
                                <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>Total Records = Inserts + Updates + Unchanged</Typography>
                                <Typography variant="caption" display="block"><Box component="span" sx={{ color: '#66bb6a', fontWeight: 600 }}>+Green</Box> — Records inserted (new)</Typography>
                                <Typography variant="caption" display="block"><Box component="span" sx={{ color: '#ffa726', fontWeight: 600 }}>~Orange</Box> — Records updated (changed)</Typography>
                                <Typography variant="caption" display="block"><Box component="span" sx={{ color: '#42a5f5', fontWeight: 600 }}>=Blue</Box> — Records unchanged (skipped)</Typography>
                              </Box>
                            }
                            arrow
                            placement="top"
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell sx={headerCellStyle}>
                        <TableSortLabel
                          active={sortBy === 'status'}
                          direction={sortBy === 'status' ? sortDirection : 'asc'}
                          onClick={() => handleSort('status')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: sortBy === 'status' ? `${theme.palette.primary.main} !important` : undefined
                            }
                          }}
                        >
                          Status
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" sx={headerCellStyle}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody
                    sx={{
                      '& .MuiTableRow-root:nth-of-type(odd)': {
                        backgroundColor: alpha(theme.palette.background.default, 0.4),
                      }
                    }}
                  >
                    {history.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography color="text.secondary" sx={{ py: 4 }}>
                            {hasActiveFilters
                              ? 'No sync history matches your filters.'
                              : 'No sync history found.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      history.map((item) => {
                        const stats = item.stats || {};
                        const totalInserted = stats.totalInserted ?? stats.phases?.menuSync?.totalInserted ?? 0;
                        const totalUpdated = stats.totalUpdated ?? stats.phases?.menuSync?.totalUpdated ?? 0;
                        const totalSkipped = stats.totalSkipped ?? stats.phases?.menuSync?.totalSkipped ?? 0;
                        const tableCount = (item.tablesSynced || []).length;
                        const isRunning = ['IN_PROGRESS', 'RUNNING'].includes((item.status || '').toUpperCase());

                        return (
                          <TableRow
                            key={item.id}
                            hover
                            sx={{
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: `${alpha(theme.palette.primary.light, 0.08)} !important`,
                                transform: 'translateY(-1px)'
                              },
                              ...(isRunning && {
                                backgroundColor: `${alpha(theme.palette.info.main, 0.04)} !important`,
                                animation: `${pulseAnimation} 2s ease-in-out infinite`,
                              }),
                            }}
                          >
                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{
                                  maxWidth: 200,
                                  color: theme.palette.primary.main
                                }}
                                noWrap
                              >
                                {item.syncName || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={item.syncType}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontWeight: 500,
                                  borderRadius: '8px'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {item.sourceAccountId}
                              </Typography>
                              {item.sourceConfigId && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Config #{item.sourceConfigId}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {item.targetAccountId || item.accountId}
                              </Typography>
                              {item.targetConfigId && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Config #{item.targetConfigId}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {item.menuId || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                {totalInserted > 0 && (
                                  <Box component="span" sx={{ color: 'success.main', fontWeight: 500 }}>+{totalInserted.toLocaleString()}</Box>
                                )}
                                {totalUpdated > 0 && (
                                  <Box component="span" sx={{ color: 'warning.main', fontWeight: 500, ml: totalInserted > 0 ? 0.5 : 0 }}>~{totalUpdated.toLocaleString()}</Box>
                                )}
                                {totalSkipped > 0 && (
                                  <Box component="span" sx={{ color: 'info.main', fontWeight: 500, ml: (totalInserted > 0 || totalUpdated > 0) ? 0.5 : 0 }}>={totalSkipped.toLocaleString()}</Box>
                                )}
                                {totalInserted === 0 && totalUpdated === 0 && totalSkipped === 0 && '—'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {(totalInserted + totalUpdated + totalSkipped) > 0
                                  ? `${(totalInserted + totalUpdated + totalSkipped).toLocaleString()} records, ${tableCount} table${tableCount !== 1 ? 's' : ''}`
                                  : `${tableCount} table${tableCount !== 1 ? 's' : ''}`
                                }
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getStatusIcon(item.status)}
                                <Chip
                                  label={item.status}
                                  size="small"
                                  color={getStatusColor(item.status)}
                                  variant="outlined"
                                  sx={{
                                    fontWeight: 500,
                                    borderRadius: '8px'
                                  }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                <Tooltip title="View Details">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewDetails(item)}
                                    color="primary"
                                    sx={{
                                      transition: 'all 0.2s ease',
                                      '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        transform: 'scale(1.1)'
                                      }
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {item.rollbackAvailable && (item.status === 'completed' || item.status === 'COMPLETED') && (
                                  <Tooltip title="Rollback">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRollbackClick(item)}
                                      color="warning"
                                      sx={{
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                          backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                          transform: 'scale(1.1)'
                                        }
                                      }}
                                    >
                                      <UndoIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Custom Pagination matching Accounts style */}
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                  backgroundColor: theme.palette.background.default
                }}
              >
                <Box display="flex" alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{
                      mr: 2,
                      color: alpha(theme.palette.text.primary, 0.7),
                      fontWeight: 500
                    }}
                  >
                    Items per page:
                  </Typography>
                  <FormControl
                    size="small"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04)
                        }
                      }
                    }}
                  >
                    <Select
                      value={rowsPerPage}
                      onChange={handleChangeRowsPerPage}
                      sx={{
                        height: 36,
                        minWidth: 70,
                        fontWeight: 500,
                        '& .MuiSelect-select': {
                          py: 0.7,
                          px: 1.5
                        }
                      }}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box display="flex" alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: alpha(theme.palette.text.primary, 0.7)
                    }}
                  >
                    {getPaginationInfo()}
                  </Typography>
                  <Box ml={1.5} display="flex" alignItems="center">
                    <Tooltip title="Previous page">
                      <span>
                        <IconButton
                          disabled={page === 0}
                          onClick={() => handlePageChange(page - 1)}
                          sx={{
                            mx: 0.5,
                            transition: 'all 0.2s',
                            color: page === 0 ? undefined : theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                              transform: 'scale(1.05)'
                            }
                          }}
                          size="small"
                        >
                          <NavigateBeforeIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Next page">
                      <span>
                        <IconButton
                          disabled={(page + 1) * rowsPerPage >= totalCount}
                          onClick={() => handlePageChange(page + 1)}
                          sx={{
                            mx: 0.5,
                            transition: 'all 0.2s',
                            color: (page + 1) * rowsPerPage >= totalCount ? undefined : theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                              transform: 'scale(1.05)'
                            }
                          }}
                          size="small"
                        >
                          <NavigateNextIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Fade>

      {/* Detail Dialog */}
      <SyncDetailDialog
        open={detailDialogOpen}
        history={selectedHistory}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedHistory(null);
        }}
      />

      {/* Rollback Confirmation Dialog */}
      <ConfirmationDialog
        open={rollbackDialogOpen}
        title="Confirm Rollback"
        content={
          <Box>
            <Typography gutterBottom>
              Are you sure you want to rollback this sync operation?
            </Typography>
            {historyToRollback && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: alpha(theme.palette.warning.main, 0.1), borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Sync ID:</strong> {historyToRollback.syncId}
                </Typography>
                <Typography variant="body2">
                  <strong>Source Account:</strong> {historyToRollback.sourceAccountId}
                </Typography>
                <Typography variant="body2">
                  <strong>Target Account:</strong> {historyToRollback.targetAccountId || historyToRollback.accountId}
                </Typography>
                {historyToRollback.menuId && (
                  <Typography variant="body2">
                    <strong>Menu ID:</strong> {historyToRollback.menuId}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Tables:</strong> {(historyToRollback.tablesSynced || []).length}
                </Typography>
              </Box>
            )}
            <Alert severity="warning" sx={{ mt: 2 }}>
              This will restore the target database to its previous state. This action cannot be undone.
            </Alert>
          </Box>
        }
        confirmButtonText={rollingBack ? 'Rolling back...' : 'Rollback'}
        severity="warning"
        onConfirm={handleRollbackConfirm}
        onCancel={() => {
          setRollbackDialogOpen(false);
          setHistoryToRollback(null);
        }}
      />
    </Box>
  );
};

export default HistoryTab;
